import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  VStack,
  Text,
  Spinner,
  Flex,
  useDisclosure,
} from "@chakra-ui/react";
import showToast from "hooks/useCustomToast";
import DocModal from "../../components/new_darwin_eva/DocModal";
import EvaLeftPanel from "../../components/new_darwin_eva/EvaLeftPanel";
import EvaRightPanelWrapper from "../../components/new_darwin_eva/EvaRightPanelWrapper";
import { DarwinAgent, DarwinTool, PromptProto } from "build-ts/darwin_agent";
import { JinjaEnv } from "build-ts/jinja";
import { getAgentById, getToolsList, updateAgent, getSystemPromptDocumentation } from "services/NewDarwinEvaServices";
import { darwinAgentTypeFromJSON } from "build-ts/common";

const EvaDetailPage: React.FC = () => {
  const { agent_id } = useParams<{ agent_id: string }>();

  const [promptEditor, setPromptEditor] = useState("");
  const [agentConfig, setAgentConfig] = useState<DarwinAgent>({
    header: {
      id: "",
      name: "",
      description: "",
      type: "",
      is_active: false,
    },
    prompts: [],
    tools: [],
  });

  // Separate prompt configuration state
  const [promptConfig, setPromptConfig] = useState<PromptProto>({
    header: {
      id: "",
      name: "",
      description: "",
      is_active: false,
    },
    model_config: {
      model_name: "gpt-4o",
      model_provider: "openai",
      temperature: 0,
    },
    template: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableTools, setAvailableTools] = useState<DarwinTool[]>([]);
  const [selectedTools, setSelectedTools] = useState<DarwinTool[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [originalAgentConfig, setOriginalAgentConfig] = useState<DarwinAgent | null>(null);
  const [originalPromptConfig, setOriginalPromptConfig] = useState<PromptProto | null>(null);
  const [originalPromptEditor, setOriginalPromptEditor] = useState("");
  const [originalSelectedTools, setOriginalSelectedTools] = useState<DarwinTool[]>([]);

  // Documentation modal states
  const [availableVariables, setAvailableVariables] = useState<JinjaEnv | null>(null);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const { isOpen: isDocsOpen, onOpen: onDocsOpen, onClose: onDocsClose } = useDisclosure();

  const fetchToolsList = async () => {
    try {
      const params = new URLSearchParams({
        limit: "100",
        offset: "0",
        sort_by: "name",
        sort_order: "asc",
      });

      const data = await getToolsList(params);
      return data.tools || [];
    } catch (error) {
      console.error("Error fetching tools list:", error);
      showToast("Failed to fetch tools list", "", "error");
      throw error;
    }
  };

  // API call to fetch system prompt documentation
  const fetchSystemPromptDocumentation = async () => {
    try {
      setIsLoadingDoc(true);
      const data = await getSystemPromptDocumentation();
      // Set the JinjaEnv data directly from the API response
      if (data) {
        setAvailableVariables(data);
      } else {
        setAvailableVariables(null);
      }
    } catch (error) {
      console.error("Error fetching system prompt documentation:", error);
      showToast("Failed to fetch documentation", "", "error");
      setAvailableVariables(null);
    } finally {
      setIsLoadingDoc(false);
    }
  };

  const handleViewDocs = async () => {
    onDocsOpen();
    // Fetch documentation when modal opens
    await fetchSystemPromptDocumentation();
  };

  // Load agent and prompt data on component mount
  useEffect(() => {
    const loadData = async () => {
      if (!agent_id) {
        showToast("No agent ID provided", "", "error");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch agent data and tools list in parallel
        const [agentData, toolsData] = await Promise.all([
          getAgentById(agent_id),
          fetchToolsList(),
        ]);

        // Check if agent data was successfully fetched
        if (!agentData) {
          showToast("Error", "Failed to load agent data", "error");
          return;
        }

        const agentInitialState: DarwinAgent = {
          header: {
            id: agentData.header?.id || "",
            name: agentData.header?.name?.trim() || "",
            description: agentData.header?.description?.trim() || "",
            type: agentData.header?.type || "",
            is_active: agentData.header?.is_active ?? false,
            created_date: agentData.header?.created_date || 0,
            last_modified_date: agentData.header?.last_modified_date || 0,
          },
          prompts: agentData.prompts || [],
          tools: agentData.tools || [],
        };
        setAgentConfig({
          ...agentInitialState
        });

        // Initialize separate prompt config state from first prompt
        const firstPrompt = agentData.prompts?.[0];
        const promptInitialState: PromptProto = {
          header: {
            id: firstPrompt?.header?.id || "",
            name: firstPrompt?.header?.name || "",
            description: firstPrompt?.header?.description || "",
            is_active: firstPrompt?.header?.is_active ?? false,
            created_date: firstPrompt?.header?.created_date || 0,
            last_modified_date: firstPrompt?.header?.last_modified_date || 0,
          },
          model_config: {
            model_name: firstPrompt?.model_config?.model_name || "gpt-4o",
            model_provider: firstPrompt?.model_config?.model_provider || "openai",
            temperature: firstPrompt?.model_config?.temperature ?? 0,
          },
          template: firstPrompt?.template || "",
        };
        setPromptConfig(promptInitialState);

        // Initialize selectedTools from agent data
        setSelectedTools(agentData.tools?.map((tool: DarwinTool) => tool) || []);

        // Set prompt editor from template if available
        setPromptEditor(firstPrompt?.template || "");

        setAvailableTools(toolsData);

        // Store original state for cancel functionality
        setOriginalAgentConfig({
          ...agentInitialState
        });
        setOriginalPromptConfig(promptInitialState);
        setOriginalPromptEditor(firstPrompt?.template || "");
        setOriginalSelectedTools(agentData.tools?.map((tool: DarwinTool) => tool) || []);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent_id]);

  const handleConfigChange = (field: string, value: any) => {
    setAgentConfig(prev => {
      // Handle nested field updates (e.g., "header.name")
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return {
            ...prev,
            header: {
              ...prev.header,
              [childField]: value
            }
          };
        }
      }

      // Handle other direct field updates
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleToolsChange = (selectedToolsArray: (DarwinTool | any)[]) => {
    setSelectedTools(selectedToolsArray as DarwinTool[]);
    setAgentConfig(prev => ({
      ...prev,
      tools: selectedToolsArray as DarwinTool[]
    }));
  };

  const handlePromptEditorChange = (value: string) => {
    setPromptEditor(value);
    setPromptConfig(prev => ({
      ...prev,
      template: value
    }));
  };

  const handlePromptConfigChange = (field: string, value: any) => {
    setPromptConfig(prev => {
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        if (parentField === "header") {
          return {
            ...prev,
            header: {
              ...prev.header,
              [childField]: value
            }
          };
        } else if (parentField === "model_config") {
          return {
            ...prev,
            model_config: {
              ...prev.model_config,
              [childField]: value
            }
          };
        }
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };


  const handleEdit = () => {
    // Store original state
    setOriginalAgentConfig({ ...agentConfig });
    setOriginalPromptConfig({ ...promptConfig });
    setOriginalPromptEditor(promptEditor);
    setOriginalSelectedTools([...selectedTools]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Restore original state
    if (originalAgentConfig) {
      setAgentConfig(originalAgentConfig);
    }
    if (originalPromptConfig) {
      setPromptConfig(originalPromptConfig);
    }
    setPromptEditor(originalPromptEditor);
    setSelectedTools(originalSelectedTools);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const result = await updateAgent(agent_id, agentConfig, selectedTools);

    // Only update local state if the update was successful
    if (result) {
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return (
      <Box className="eva-container eva-small-scrollbar" display="flex" justifyContent="center" alignItems="center" minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading agent data...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box className="eva-container eva-small-scrollbar">
      <Flex className="eva-main-layout">
        {/* Left Panel - Agent Configuration */}
        <EvaLeftPanel
          agent={agentConfig}
          availableTools={availableTools}
          onConfigChange={handleConfigChange}
          onToolsChange={handleToolsChange}
          isEditing={isEditing}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSave={handleSave}
          onViewDocs={handleViewDocs}
        />

        {/* Right Panel - Prompt Editor */}
        <EvaRightPanelWrapper
          agent={agentConfig}
          promptConfig={promptConfig}
          promptEditor={promptEditor}
          onPromptEditorChange={handlePromptEditorChange}
          onPromptConfigChange={handlePromptConfigChange}
          analysis_flag=""
        />
      </Flex>

      {/* Variables Documentation Modal */}
      <DocModal
        isDocsOpen={isDocsOpen}
        onDocsClose={onDocsClose}
        isLoadingDoc={isLoadingDoc}
        availableVariables={availableVariables}
        agentType={agentConfig.header?.type ? darwinAgentTypeFromJSON(agentConfig.header.type) : undefined}
      />
    </Box>
  );
};

export default EvaDetailPage;
